import { ActivatedRoute } from '@angular/router';
import { Component, Input, OnInit, HostListener, OnDestroy, ViewChild } from '@angular/core';

import { awaitHandler } from '@utils/awaitHandler';
import { Events } from '@services/events/events.service';
import { FoldersService } from '../../services/folders.service';
import { FilterData } from '@shared/modules/menu-filter/services/filter-menu.service';
import { ICopyConfig } from '@shared/modules/copy-link/components/copy-link/copy-link.component';
import { FoldersDataService, Permission, PermissionsService, UserdataService, UserService } from '@services';
import { ContentFolderService } from '@modules/management/pages/list/content/services/content-folder.service';
import { ActiveFiltersComponent } from '@shared/modules/menu-filter/components/active-filters/active-filters.component';
import {
  FolderDataType,
  FolderListFilter,
  IFolder
} from '@modules/management/modules/folders/model/folders.interfaces';

import { get, keys } from 'lodash';


@Component({
  selector: 'app-folder-header',
  templateUrl: './folder-header.component.html',
  styleUrls: ['./folder-header.component.scss'],
})
export class FolderHeaderComponent implements OnInit, OnDestroy {
  @ViewChild(ActiveFiltersComponent) activeFiltersComponent: ActiveFiltersComponent;
  @Input() title: string;
  @Input() mainUrl: string;
  @Input() allowCopy: boolean;
  @Input() filter: FolderListFilter;

  public folderData: IFolder;
  public parentFolderData: IFolder;
  public dropDownData: IFolder[];
  public parentTitle: string;
  public showDropDown = false;
  public parentFolderID = 0;
  public canEdit = true;
  public copyConfig: ICopyConfig;

  constructor(
    private foldersService: FoldersService,
    private foldersDataService: FoldersDataService,
    private activatedRoute: ActivatedRoute,
    private events: Events,
    private userdataService: UserdataService,
    private permissions: PermissionsService,
    private userService: UserService,
    private contentFolderService: ContentFolderService
  ) {}

  clickDropdownTrigger($event): void {
    if (!this.showDropDown) {
      $event.stopPropagation();
      this.showDropDown = true;
    }
  }

  @HostListener('document:click')
  clickOutside(): void {
    if (this.showDropDown) {
      this.showDropDown = false;
    }
  }

  ngOnDestroy() {
    this.events.unsubscribe('ccs:foldersUpdate', this.foldersUpdateListener);
  }

  async ngOnInit(): Promise<void> {
    this.initFilter();
    this.parentFolderID = +get(this.activatedRoute.snapshot.params, 'folderId') || 0;
    await awaitHandler(this.refreshParentsData());
    this.updateFoldersData();
    this.events.subscribe('ccs:foldersUpdate', this.foldersUpdateListener);
    this.initCopyLinkConfig();
  }

  backNavigation(): void {
    this.foldersService.goToFolder(this.mainUrl, this.folderData?.parentID || 0);
  }

  editFolder(): void {
    this.foldersService.openFolderView({
      folderID: this.parentFolderID,
      folderType: this.folderData.type
    });
  }

  onDropdownItemClick(item): void {
    this.foldersService.goToFolder(this.mainUrl, item.folderID);
  }

  public onFilterChange(filterData: FilterData) {
    this.foldersService.saveFilterData(filterData);
    this.filter.filterData = filterData;
    this.filter.onChange(this.filter.filterData);
  }

  public initFilter() {
    const filterData = this.foldersService.getFilterData();

    if (this.filter?.config) {
      this.filter.config.data = this.filter.filterData = filterData;
      this.activeFiltersComponent?.ngOnInit();
    }
  }

  private foldersUpdateListener = () => {
    this.updateFoldersData();
  };

  private updateFoldersData(): void {
    this.parentTitle = this.title;
    this.getFolderData();

    if (this.folderData?.parentID) {
      this.parentFolderData = this.foldersDataService.getFolderByID(this.folderData.parentID);
      this.parentTitle = this.userService.translateItem(this.parentFolderData, 'title');
      this.dropDownData = this.getFoldersList();
    }
    let editable = true;
    if (this.folderData?.attributes) {
      if (this.folderData.type === FolderDataType.CONTENT && this.folderData.attributes.owner_only) {
        // only the owner can edit this folder.
        editable = false;
        if (this.permissions.canView(Permission.SuperAdmin)) {
          editable = true;
        }
        if (this.folderData.creator === +this.userdataService.userID) {
          editable = true;
        }
      }
    }
    this.canEdit = editable;
  }

  private getFolderData(): void {
    this.folderData = this.foldersDataService.getFolderByID(this.parentFolderID);
  }

  private getFoldersList(): IFolder[] {
    let parentsList: IFolder[] = [];

    if (this.parentFolderData?.parentID) {
      let checkedFolder = this.foldersDataService.getFolderByID(this.parentFolderData.parentID);
      parentsList.unshift(checkedFolder);

      while (checkedFolder.parentID) {
        checkedFolder = this.foldersDataService.getFolderByID(checkedFolder.parentID);
        parentsList.unshift(checkedFolder);
      }
    }

    parentsList.unshift({
      folderID: 0,
      title: this.title,
      timestamp: null,
      status: null,
      statusText: null,
      parentID: null,
      type: null,
      active: null,
      creator: null,
      created: null,
      lastUpdate: null,
      disabledAt: null,
      disabledBy: null,
      attributes: { document_review: 0 }
    });

    return parentsList;
  }

  private async refreshParentsData(): Promise<void> {
    if (!keys(this.foldersDataService.getFoldersByType(this.folderData.type)).length) {
      await awaitHandler(this.foldersDataService.refresh());
    }
  }

  private initCopyLinkConfig() {
    this.copyConfig = {
      url: this.contentFolderService.getCopyFolderLink(this.parentFolderID)
    };
  }
}
